import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import CreateAuditModal from '../../components/CreateAuditModal';

const ITEMS_PER_PAGE = 25;

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-4 md:p-6 2xl:p-10">
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
      <strong className="font-semibold">Error:</strong>
      <span className="block sm:inline ml-1">{message}</span>
    </div>
  </div>
);

const formatDate = (date) => {
  if (!date) return 'N/A';
  const auditDate = new Date(date);
  return auditDate.toLocaleDateString() + ' ' + auditDate.toLocaleTimeString();
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return 'N/A';
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
};

const getStatusBadge = (status) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-100 dark:bg-gray-600', text: 'text-gray-800 dark:text-gray-200', label: 'Draft' },
    in_progress: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200', label: 'In Progress' },
    completed: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200', label: 'Completed' }
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const AuditsPage = () => {
  const [audits, setAudits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAudits = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Get the current user for organization access
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          throw new Error('Authentication error: ' + userError.message);
        }
        if (!user) {
          throw new Error('Not authenticated. Please log in.');
        }

        // Get user's organization
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) {
          throw new Error('Failed to get organization: ' + membershipsError.message);
        }
        
        if (!memberships || memberships.length === 0) {
          throw new Error('No organization found. Please contact your administrator.');
        }

        const organizationId = memberships[0].organization_id;

        // Build query - exclude soft-deleted audits
        let query = supabase
          .from('audits')
          .select(`
            id,
            name,
            percentage,
            start_date,
            end_date,
            total_calls_in_range,
            selected_calls_count,
            status,
            created_at,
            created_by
          `)
          .eq('organization_id', organizationId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        // Apply status filter if selected
        if (statusFilter) {
          query = query.eq('status', statusFilter);
        }

        const { data: auditsData, error: auditsError } = await query;

        if (auditsError) {
          throw new Error('Failed to fetch audits: ' + auditsError.message);
        }

        setAudits(auditsData || []);

      } catch (e) {
        console.error("Failed to fetch audits:", e);
        setError(e.message);
        setAudits([]);
      }
      setIsLoading(false);
    };

    fetchAudits();
  }, [statusFilter]);

  // Filter audits based on status
  const filteredAudits = audits;

  // Pagination
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentAudits = filteredAudits.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAudits.length / ITEMS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handleCreateAudit = () => {
    setIsCreateModalOpen(true);
  };

  const handleViewAudit = (auditId) => {
    navigate(`/dashboard/audits/${auditId}`);
  };

  const handleDeleteAudit = async (auditId, auditName) => {
    if (!window.confirm(`Are you sure you want to delete "${auditName || 'this audit'}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('audits')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', auditId);

      if (error) {
        throw error;
      }

      // Remove the deleted audit from the local state
      setAudits(prevAudits => prevAudits.filter(audit => audit.id !== auditId));
    } catch (error) {
      console.error('Failed to delete audit:', error);
      alert('Failed to delete audit: ' + error.message);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-2">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            Audits
          </h1>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <button
          onClick={handleCreateAudit}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition-colors"
        >
          + Create New Audit
        </button>
      </div>

      {filteredAudits.length === 0 ? (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg p-6">
          <p className="text-gray-700 dark:text-gray-300">No audits found.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-boxdark shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Range</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Percentage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Calls</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Selected</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-boxdark divide-y divide-gray-200 dark:divide-gray-700">
                {currentAudits.map((audit) => (
                  <tr key={audit.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {audit.name || 'Unnamed Audit'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatDateRange(audit.start_date, audit.end_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {audit.percentage}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {audit.total_calls_in_range}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {audit.selected_calls_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(audit.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {formatDate(audit.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleViewAudit(audit.id)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteAudit(audit.id, audit.name)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"
                          title="Delete Audit"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-white dark:bg-boxdark px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-boxdark hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-boxdark hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, filteredAudits.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredAudits.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900 border-indigo-500 dark:border-indigo-600 text-indigo-600 dark:text-indigo-300'
                            : 'bg-white dark:bg-boxdark border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <CreateAuditModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};

export default AuditsPage;

